import React, { useState } from 'react';
import { TabState } from '../../types';
import { Plus } from 'lucide-react';
import { ContextHelpButtonSimple } from '../components/ContextHelp/ContextHelpButtonSimple';
import * as styles from './TabContainer.css.ts';

/**
 * TabBar
 * 
 * タブバーのUI部分のみを担当
 * ドラッグ&ドロップやタブの表示を処理
 */

interface TabBarProps {
  tabs: TabState[];
  activeTabId: string;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  onTabDragStart?: (tabId: string, event: React.DragEvent) => void;
  onTabDragEnd?: () => void;
  onTabDrop?: (draggedTabId: string, targetIndex: number) => void;
  // タブタイトルのカスタマイズ関数（オプション）
  getTabTitle?: (tab: TabState) => string;
  // コンテキストメニューハンドラー
  onTabContextMenu?: (tab: TabState, event: React.MouseEvent) => void;
  // ハイライト中のタブID
  highlightingTabId?: string | null;
  // 新しいタブを追加するハンドラー
  onAddTab?: () => void;
  // ヘルプコンテンツ（MiniHeaderで表示する）
  helpContent?: React.ReactNode;
  // ヘルプボタンのaria-label
  helpAriaLabel?: string;
}

export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onTabDragStart,
  onTabDragEnd,
  onTabDrop,
  getTabTitle = (tab) => tab.title,
  onTabContextMenu,
  highlightingTabId,
  onAddTab,
  helpContent,
  helpAriaLabel
}) => {
  const [dragOverTabIndex, setDragOverTabIndex] = useState<number | null>(null);
  
  const handleTabClick = (tabId: string) => {
    onTabClick(tabId);
  };
  
  const handleTabClose = (tabId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onTabClose(tabId);
  };
  
  const handleDragStart = (tabId: string, event: React.DragEvent) => {
    event.dataTransfer.setData('text/plain', tabId);
    event.dataTransfer.effectAllowed = 'move';
    onTabDragStart?.(tabId, event);
  };
  
  const handleDragEnd = () => {
    onTabDragEnd?.();
  };
  
  const handleTabDragOver = (event: React.DragEvent, targetIndex: number) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverTabIndex(targetIndex);
  };
  
  const handleTabDrop = (event: React.DragEvent, targetIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    const draggedTabId = event.dataTransfer.getData('text/plain');
    if (draggedTabId && draggedTabId !== '') {
      onTabDrop?.(draggedTabId, targetIndex);
    }
    setDragOverTabIndex(null);
  };
  
  const handleDragLeave = () => {
    setDragOverTabIndex(null);
  };
  
  // タブが1つしかない場合はミニヘッダーを表示
  if (tabs.length === 1) {
    const tab = tabs[0];
    return (
      <div
        className={`${styles.miniHeader} ${highlightingTabId === tab.id ? styles.highlighting : ''}`}
        onContextMenu={(e) => onTabContextMenu?.(tab, e)}
        onDragStart={(e) => handleDragStart(tab.id, e)}
        onDragEnd={handleDragEnd}
        draggable
        data-testid={`mini-header-${tab.id}`}
        role="tab"
        aria-selected={true}
        tabIndex={0}
        title={getTabTitle(tab)}
      >
        <span className={styles.miniHeaderTitle}>{getTabTitle(tab)}</span>
        {tab.isClosable && (
          <button
            className={styles.closeButton}
            onClick={(e) => handleTabClose(tab.id, e)}
            data-testid={`close-tab-${tab.id}`}
            aria-label={`Close ${getTabTitle(tab)}`}
          >
            ×
          </button>
        )}
        {onAddTab && (
          <button
            className={styles.addTabButton}
            onClick={(e) => {
              e.stopPropagation();
              onAddTab();
            }}
            aria-label="Add new tab"
            data-testid="mini-add-tab-button"
          >
            <Plus size={16} />
          </button>
        )}
        {helpContent && (
          <div onClick={(e) => e.stopPropagation()}>
            <ContextHelpButtonSimple helpContent={helpContent} ariaLabel={helpAriaLabel} />
          </div>
        )}
      </div>
    );
  }
  
  // 複数タブの場合は通常のタブバー
  return (
    <div className={styles.tabBar} data-testid="tab-bar">
      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          className={`${styles.tab} ${tab.id === activeTabId ? styles.active : ''} ${dragOverTabIndex === index ? styles.dragOver : ''} ${highlightingTabId === tab.id ? styles.highlighting : ''}`}
          onClick={() => handleTabClick(tab.id)}
          onContextMenu={(e) => onTabContextMenu?.(tab, e)}
          onDragStart={(e) => handleDragStart(tab.id, e)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleTabDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleTabDrop(e, index)}
          draggable
          data-testid={`tab-${tab.id}`}
          role="tab"
          aria-selected={tab.id === activeTabId}
          tabIndex={0}
          title={getTabTitle(tab)}
        >
          <span className={styles.tabTitle}>{getTabTitle(tab)}</span>
          {tab.isClosable && (
            <button
              className={styles.closeButton}
              onClick={(e) => handleTabClose(tab.id, e)}
              data-testid={`close-tab-${tab.id}`}
              aria-label={`Close ${getTabTitle(tab)}`}
            >
              ×
            </button>
          )}
        </div>
      ))}
      {onAddTab && (
        <button
          className={styles.addTabButton}
          style={{ marginLeft: 'auto' }}
          onClick={onAddTab}
          aria-label="Add new tab"
          data-testid="add-tab-button"
        >
          <Plus size={16} />
        </button>
      )}
      {helpContent && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            borderRight: `1px solid var(--color-border)`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextHelpButtonSimple helpContent={helpContent} ariaLabel={helpAriaLabel} />
        </div>
      )}
    </div>
  );
};

export default React.memo(TabBar);